import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useAuth } from '../../src/context/AuthContext';
import { useStripeSubscription } from '../../src/hooks/useStripeSubscription';
import { cn } from '../../src/lib/utils';
import { useLanguage } from '../../src/functions/useLanguage';
import { getCheckoutUrl, getStarterCheckoutUrl, type StripePlanId } from '../../src/lib/stripeBilling';
import { wt } from '../i18n';
import { usePricingSymbol } from '../lib/usePricingSymbol';

type BillingCycle = 'monthly' | 'yearly';

interface PlanFeature {
  key: Parameters<typeof wt>[1];
  included: boolean;
}

interface PlanDef {
  id: string;
  nameKey: Parameters<typeof wt>[1];
  priceKey: Parameters<typeof wt>[1];
  monthlyPrice: string;
  yearlyPrice: string;
  subtitleKey: Parameters<typeof wt>[1];
  features: PlanFeature[];
  highlighted: boolean;
  badge?: Parameters<typeof wt>[1];
}

const PLANS: PlanDef[] = [
  {
    id: 'free',
    nameKey: 'pricing.free.name',
    priceKey: 'pricing.free.price',
    monthlyPrice: '',
    yearlyPrice: '',
    subtitleKey: 'pricing.free.subtitle',
    highlighted: false,
    features: [
      { key: 'pricing.feat.onePet',      included: true  },
      { key: 'pricing.feat.vitals',      included: true  },
      { key: 'pricing.feat.meds',        included: true  },
      { key: 'pricing.feat.guide',       included: true  },
      { key: 'pricing.feat.fellowship',  included: true  },
      { key: 'pricing.feat.trends',      included: true  },
      { key: 'pricing.feat.vault5',      included: true  },
      { key: 'pricing.feat.vetExport',   included: false },
    ],
  },
  {
    id: 'vital',
    nameKey: 'pricing.vital.name',
    priceKey: 'pricing.vital.name',
    monthlyPrice: '$9.99',
    yearlyPrice: '$7.50',
    subtitleKey: 'pricing.vital.subtitle',
    highlighted: true,
    badge: 'pricing.mostPopular',
    features: [
      { key: 'pricing.feat.vitalPets',   included: true  },
      { key: 'pricing.feat.coCarers',    included: true  },
      { key: 'pricing.feat.vitals',      included: true  },
      { key: 'pricing.feat.meds',        included: true  },
      { key: 'pricing.feat.guide',       included: true  },
      { key: 'pricing.feat.fellowship',  included: true  },
      { key: 'pricing.feat.trends',      included: true  },
      { key: 'pricing.feat.vault50',     included: true  },
      { key: 'pricing.feat.vetExport',   included: true  },
      { key: 'pricing.feat.digest',      included: false },
    ],
  },
  {
    id: 'lifetime',
    nameKey: 'pricing.lifetime.name',
    priceKey: 'pricing.lifetime.name',
    monthlyPrice: '$24.99',
    yearlyPrice: '$20.83',
    subtitleKey: 'pricing.lifetime.subtitle',
    highlighted: false,
    features: [
      { key: 'pricing.feat.unlimitedPets',  included: true },
      { key: 'pricing.feat.coCarers',       included: true },
      { key: 'pricing.feat.vitals',         included: true },
      { key: 'pricing.feat.meds',           included: true },
      { key: 'pricing.feat.guide',          included: true },
      { key: 'pricing.feat.fellowship',     included: true },
      { key: 'pricing.feat.customTrends',   included: true },
      { key: 'pricing.feat.vault150',       included: true },
      { key: 'pricing.feat.vetExport',      included: true },
      { key: 'pricing.feat.digest',         included: true },
    ],
  },
];

export function Pricing() {
  const { language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const [cycle, setCycle] = useState<BillingCycle>('yearly');
  const currencySymbol = usePricingSymbol();
  /** Swap the leading '$' in a stored price string for the local symbol. */
  const localPrice = (price: string) => price.replace(/^\$/, currencySymbol);
  const { subscription } = useStripeSubscription(user?.id);

  // Determine which plan the user currently holds
  const currentPlanId = (() => {
    if (!isAuthenticated) return null;
    const id = (subscription?.planId ?? '').toLowerCase();
    if (id === 'lifetime') return 'lifetime';
    if (id === 'vital') return 'vital';
    const name = (subscription?.planName ?? '').toLowerCase();
    if (name.includes('lifetime') || name.includes('expert companion')) return 'lifetime';
    if (name.includes('vital')) return 'vital';
    // Authenticated with no paid subscription → on free/trial
    return 'free';
  })();

  const highlightedPlanId = currentPlanId === 'lifetime' ? 'lifetime' : 'vital';

  const appHost = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';
  const postCheckoutUrl = `${appHost}/records`;

  const handlePlanClick = (planId: string) => {
    if (!isAuthenticated || !user) {
      if (planId === 'free') {
        window.location.href = `${appHost}/register`;
      } else {
        window.location.href = `${appHost}/register?plan=${encodeURIComponent(planId)}&cycle=${encodeURIComponent(cycle)}`;
      }
      return;
    }

    if (planId === 'free') {
      const starterUrl = getStarterCheckoutUrl({ email: user.email || '', uid: user.id, redirectUrl: postCheckoutUrl });
      if (starterUrl) {
        window.location.href = starterUrl;
        return;
      }
      return;
    }

    const url = getCheckoutUrl(planId as Exclude<StripePlanId, 'starter'>, cycle, {
      uid:   user.id,
      email: user.email,
      redirectUrl: postCheckoutUrl,
    });

    if (!url) {
      alert('Checkout is not available yet for this plan. Please try again soon.');
      return;
    }

    window.location.href = url;
  };

  return (
    <section id="pricing" className="pt-24 pb-24 mb-24 bg-primary-brown/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 mb-16">
          <div>
            <h2 className="text-4xl lg:text-5xl text-title-primary mb-1 uppercase tracking-tight">
              {language === 'English' ? (
                <><span className="font-bold">Choose </span><span className="font-light">Your </span><span className="font-bold">Fellowship</span></>
              ) : (
                <span className="font-bold">{t('pricing.heading')}</span>
              )}
            </h2>
            <p className="text-gray-500 text-sm max-w-lg">{t('pricing.subheading')}</p>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center gap-1 bg-white rounded-full p-1 shadow-sm self-start shrink-0">
            <button
              onClick={() => setCycle('monthly')}
              className={cn(
                'px-5 py-2 rounded-full text-xs font-bold tracking-widest transition-all',
                cycle === 'monthly'
                  ? 'bg-primary-brown text-white shadow'
                  : 'text-gray-400 hover:text-gray-600',
              )}
            >
              {t('pricing.monthly')}
            </button>
            <button
              onClick={() => setCycle('yearly')}
              className={cn(
                'px-5 py-2 rounded-full text-xs font-bold tracking-widest transition-all',
                cycle === 'yearly'
                  ? 'bg-primary-brown text-white shadow'
                  : 'text-gray-400 hover:text-gray-600',
              )}
            >
              {t('pricing.yearly')}
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-6 items-center">
          {PLANS.map((plan) => {
            const isHighlighted = plan.id === highlightedPlanId;
            const isFree = plan.id === 'free';
            const displayPrice = isFree
              ? t('pricing.free.price')
              : cycle === 'monthly'
              ? localPrice(plan.monthlyPrice)
              : localPrice(plan.yearlyPrice);

            return (
              <div
                key={plan.id}
                className={cn(
                  'relative flex flex-col rounded-[2rem] p-8 transition-all',
                  isHighlighted
                    ? 'bg-[#0d2744] text-white scale-105 z-10'
                    : 'bg-white',
                )}
              >
                {/* Badge */}
                {plan.badge && isHighlighted && (
                  <div className="absolute -top-4 right-6 bg-primary-brown text-white text-xs font-black tracking-widest px-4 py-1.5 rounded-full border border-primary-brown/90 shadow-lg">
                    {t(plan.badge)}
                  </div>
                )}

                {/* Price block */}
                <div className="mb-8">
                  {isFree ? (
                    <p className="text-4xl font-black mb-1">{displayPrice}</p>
                  ) : (
                    <p className="text-4xl font-black mb-1">
                      {displayPrice}
                      <span className={cn('text-base font-medium ml-1', isHighlighted ? 'text-primary-brown/70' : 'text-gray-400')}>
                        {t('pricing.perMonth')}
                      </span>
                    </p>
                  )}
                  <h3 className={cn('text-xl font-black mt-2 mb-1', isHighlighted ? 'text-white' : 'text-[#0d2744]')}>
                    {t(plan.nameKey)}
                  </h3>
                  <p className={cn('text-sm', isHighlighted ? 'text-primary-brown/70' : 'text-gray-400')}>
                    {t(plan.subtitleKey)}
                  </p>
                </div>

                {/* Feature list */}
                <ul className="flex-1 space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f.key} className="flex items-center gap-3 text-sm">
                      <Check
                        size={18}
                        className={cn(
                          'shrink-0',
                          f.included
                            ? isHighlighted
                              ? 'text-primary-brown/70'
                              : 'text-primary-brown'
                            : 'text-gray-300',
                        )}
                      />
                      <span
                        className={cn(
                          f.included
                            ? isHighlighted
                              ? 'text-white font-medium'
                              : 'text-gray-700 font-medium'
                            : 'text-gray-300 line-through',
                        )}
                      >
                        {t(f.key)}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {(() => {
                  const isCurrentPlan = currentPlanId === plan.id;
                  return (
                    <button
                      onClick={() => !isCurrentPlan && handlePlanClick(plan.id)}
                      disabled={isCurrentPlan}
                      className={cn(
                        'w-full py-3.5 rounded-2xl font-bold text-sm transition-all',
                        isCurrentPlan
                          ? isHighlighted
                            ? 'bg-primary-brown/20 text-primary-brown/80 cursor-default'
                            : 'bg-gray-100 text-gray-400 cursor-default'
                          : isHighlighted
                          ? 'bg-primary-brown hover:bg-primary-brown/80 text-white shadow-lg hover:scale-[1.02] active:scale-95'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:scale-[1.02] active:scale-95',
                      )}
                    >
                      {isCurrentPlan
                        ? t('pricing.currentPlan')
                        : isFree
                        ? t('pricing.startFree')
                        : isAuthenticated
                        ? currentPlanId === 'lifetime' && plan.id === 'vital'
                          ? 'Downgrade'
                          : 'Upgrade'
                        : t('pricing.choosePlan')}
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
